import React from 'react';
import { Stack, Divider } from '@mui/material';
import { Skeleton } from '@repo/ui';
import { stateCardSurfaceSx } from 'pages/CertificatePage/components/stateCardSurface';

/**
 * Loading placeholder for a single OwnerProvenanceCard — mirrors its layout
 * (avatar + name/email, divider, label/value rows) 1:1 at the real card's
 * footprint so the skeleton → content swap causes zero layout shift
 * (working-conventions §19).
 */
export const OwnerProvenanceCardSkeleton: React.FC = () => (
  <Stack
    sx={{
      // The SAME surface object the card itself spreads, not a copy of its
      // values — a skeleton that rounds, fills or lifts differently to the card
      // it stands in for pops on load (§19). Reading from one source is what
      // makes that true by construction: the previous copy said `8px` and would
      // have kept saying it after the card moved to 12px.
      ...stateCardSurfaceSx,
      // Same reason as the card: the list owns the gap between items.
      mb: 0,
    }}
  >
    <Stack direction='row' alignItems='center' spacing={1.5}>
      <Skeleton variant='circular' width={40} height={40} />
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Skeleton variant='text' width={140} height={16} />
        <Skeleton variant='text' width={180} height={14} />
      </Stack>
    </Stack>

    <Divider sx={{ my: 1, ml: '52px', borderColor: theme => theme.palette.grey[300] }} />

    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ ml: '52px' }}>
      <Skeleton variant='text' width={80} height={14} />
      <Skeleton variant='text' width={110} height={14} />
    </Stack>
  </Stack>
);

/**
 * A stack of card skeletons matching the real list's spacing
 * (`Provenance.tsx`'s `<Stack spacing={1.5}>`).
 */
export const ProvenanceListSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <Stack spacing={1.5}>
    {Array.from({ length: count }).map((_, index) => (
      <OwnerProvenanceCardSkeleton key={index} />
    ))}
  </Stack>
);
